'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useState, useEffect, useRef, useSyncExternalStore } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '@/context/cart-context';
import { useWishlist } from '@/context/wishlist-context';
import { useAuth } from '@/context/auth-context';
import { formatPrice } from '@/lib/shopify';
import type { Money } from '@/lib/shopify/types';

interface SearchSuggestion {
  handle: string;
  title: string;
  featuredImage: { url: string; altText: string | null } | null;
  price: Money;
}

// Type definitions for mega menus
interface MegaMenuItem {
  name: string;
  href: string;
}

interface MegaMenuColumn {
  icon: string;
  title: string;
  items: MegaMenuItem[];
  viewAll?: MegaMenuItem;
}

interface MegaMenuPromo {
  title: string;
  description: string;
  cta: MegaMenuItem;
}

interface MegaMenuData {
  columns: MegaMenuColumn[];
  promo?: MegaMenuPromo;
  /** Optional shorter list for the mobile drawer (defaults to no sub-items). */
  mobileItems?: MegaMenuItem[];
}

// Mega menu data structure
const megaMenus: Record<string, MegaMenuData> = {
  'shop-by-product': {
    columns: [
      {
        icon: '👕',
        title: 'T-Shirts',
        items: [
          { name: 'Round Neck T-Shirt', href: '/products/custom-printed-round-neck-t-shirt' },
          { name: 'Long Sleeve T-Shirt', href: '/products/custom-printed-long-sleeve-t-shirt' },
          { name: 'Polo T-Shirt', href: '/products/custom-embroidered-polo-t-shirt' },
          { name: 'Sports T-Shirt', href: '/products/custom-printed-sports-t-shirt' },
          { name: 'Sport Polo T-Shirt', href: '/products/custom-embroidered-sports-polo-shirt' },
        ],
        viewAll: { name: 'All T-Shirts', href: '/collections/t-shirts' },
      },
      {
        icon: '🧥',
        title: 'Hoodies & Sweatshirts',
        items: [
          { name: 'Hoodie 80/20', href: '/products/custom-printed-hoodie-80-20' },
          { name: 'Premium Oversized Hoodie', href: '/products/custom-printed-oversized-hoodie' },
          { name: 'Sweatshirt', href: '/products/custom-printed-sweatshirt' },
        ],
        viewAll: { name: 'All Hoodies & Sweats', href: '/collections/hoodies-sweatshirts' },
      },
      {
        icon: '🧤',
        title: 'Jackets & Gilets',
        items: [
          { name: 'Softshell Jacket', href: '/products/custom-embroidered-softshell-jacket' },
          { name: 'Fleece Jacket', href: '/products/custom-embroidered-full-zip-fleece' },
          { name: 'Bodywarmer Gilet', href: '/products/custom-embroidered-bodywarmer-gilet' },
          { name: 'Gilet Softshell Two Tone', href: '/products/custom-embroidered-two-tone-gilet' },
        ],
        viewAll: { name: 'All Jackets', href: '/collections/jackets-gilets' },
      },
      {
        icon: '👖',
        title: 'Trousers & Leggings',
        items: [
          { name: 'Classic Baggy Joggers', href: '/products/custom-printed-baggy-joggers' },
          { name: 'Authentic Joggers Zipped', href: '/products/custom-printed-zipped-joggers' },
          { name: 'Sport Leggings', href: '/products/custom-printed-workout-leggings' },
        ],
        viewAll: { name: 'All Trousers', href: '/collections/trousers-leggings' },
      },
    ],
    promo: {
      title: 'Team Sports',
      description: 'Custom kits for clubs, teams & groups. Bulk discounts available.',
      cta: { name: 'Get a Quote', href: '/quote' },
    },
  },
  workwear: {
    columns: [
      {
        icon: '👨‍🍳',
        title: 'Chef Clothing',
        items: [
          { name: 'Chef Jacket', href: '/products/custom-embroidered-chef-jacket' },
          { name: "Chef's Hat", href: '/products/custom-embroidered-chef-hat' },
          { name: 'Apron with Pocket', href: '/products/custom-embroidered-apron-with-pocket' },
          { name: 'Cotton Apron', href: '/products/custom-embroidered-cotton-apron' },
        ],
        viewAll: { name: 'All Chef Clothing', href: '/collections/workwear' },
      },
      {
        icon: '🦺',
        title: 'High Visibility',
        items: [
          { name: 'High Vis Zip Hoodie', href: '/products/custom-embroidered-hi-vis-zip-hoodie' },
        ],
        viewAll: { name: 'All High Vis', href: '/collections/workwear' },
      },
      {
        icon: '🩺',
        title: 'Tunics & More',
        items: [
          { name: 'Orchid Tunic', href: '/products/custom-embroidered-beauty-tunic' },
        ],
        viewAll: { name: 'All Workwear', href: '/collections/workwear' },
      },
    ],
    promo: {
      title: 'Custom Workwear',
      description: 'Add your company logo to any workwear item. Embroidery & print available.',
      cta: { name: 'Enquire Now', href: '/quote' },
    },
  },
  kids: {
    columns: [
      {
        icon: '👶',
        title: 'Kids Clothing',
        items: [
          { name: 'Baby Body Suits', href: '/products/custom-printed-baby-bodysuits' },
          { name: 'Kids T-Shirt', href: '/products/custom-printed-kids-t-shirt' },
          { name: 'Kids Polo T-Shirt', href: '/products/custom-embroidered-kids-polo-shirt' },
          { name: 'Kids Baseball Caps', href: '/products/custom-embroidered-kids-baseball-caps' },
        ],
        viewAll: { name: 'All Kids', href: '/collections/kids' },
      },
    ],
    promo: {
      title: 'Kids Personalisation',
      description: 'Names, numbers & custom designs for little ones.',
      cta: { name: 'Design Yours', href: '/quote' },
    },
  },
  'school-uniform': {
    columns: [
      {
        icon: '📚',
        title: 'Shop by Uniform Type',
        items: [
          { name: 'School Sweatshirt', href: '/products/custom-embroidered-school-sweatshirt' },
          { name: 'School Cardigan', href: '/products/custom-embroidered-school-cardigan' },
          { name: 'Polo Shirt 3-5y', href: '/products/custom-embroidered-kids-polo-3-5y' },
          { name: 'Polo Shirt 5-11y', href: '/products/custom-embroidered-kids-polo-5-11y' },
          { name: 'Polo Shirt 12-14y', href: '/products/custom-embroidered-kids-polo-12-14y' },
          { name: 'PE T-Shirt', href: '/products/custom-printed-school-pe-t-shirt' },
          { name: 'PE Shorts', href: '/products/custom-printed-school-pe-shorts' },
          { name: 'Book Bag', href: '/products/custom-printed-school-book-bag' },
          { name: 'Book Bag with Strap', href: '/products/custom-printed-book-bag-with-strap' },
          { name: 'PE Bag', href: '/products/custom-printed-school-pe-bag' },
        ],
        viewAll: { name: 'All School Uniform', href: '/collections/school-uniform' },
      },
    ],
    promo: {
      title: 'Partnered Schools',
      description: 'We set up dedicated uniform shops for schools. Get in touch to partner.',
      cta: { name: 'Partner With Us', href: '/contact' },
    },
  },
  accessories: {
    columns: [
      {
        icon: '🧢',
        title: 'Hats & Caps',
        items: [
          { name: 'Premium Baseball Cap', href: '/products/custom-embroidered-baseball-cap' },
          { name: 'Premium Snapback Cap', href: '/products/custom-embroidered-snapback-cap' },
          { name: 'Snapback 5-Panel', href: '/products/custom-embroidered-5-panel-snapback' },
        ],
        viewAll: { name: 'All Hats & Caps', href: '/collections/accessories' },
      },
      {
        icon: '👜',
        title: 'Bags & Towels',
        items: [
          { name: 'Tote Bag', href: '/products/custom-printed-tote-bag' },
          { name: 'Large Towel', href: '/products/custom-embroidered-large-towel' },
          { name: 'Medium Towel', href: '/products/custom-embroidered-medium-towel' },
        ],
        viewAll: { name: 'All Accessories', href: '/collections/accessories' },
      },
    ],
  },
  business: {
    columns: [
      {
        icon: '🏢',
        title: 'By Industry',
        items: [
          { name: 'Construction & Trades', href: '/workwear-for-builders-and-trades' },
          { name: 'Clinics, Beauty & Wellness', href: '/embroidered-uniforms-for-clinics-and-salons' },
          { name: 'Restaurants & Hospitality', href: '/restaurant-and-hospitality-uniforms' },
          { name: 'Gyms, Clubs & Sports Teams', href: '/gym-and-sports-club-merchandise' },
          { name: 'Corporate & Office', href: '/corporate-uniforms-and-branded-merchandise' },
          { name: 'Events & Community', href: '/event-t-shirts-and-merchandise' },
        ],
        viewAll: { name: 'All Business Services', href: '/branded-workwear' },
      },
      {
        icon: '📦',
        title: 'Packages & Guide Prices',
        items: [
          { name: 'Starter Workwear Pack', href: '/business-clothing-packages#starter-workwear-pack' },
          { name: 'Trade Team Uniform Pack', href: '/business-clothing-packages#trade-team-uniform-pack' },
          { name: 'Premium Staff Uniform Pack', href: '/business-clothing-packages#premium-staff-uniform-pack' },
          { name: 'Sports Club Kit Pack', href: '/business-clothing-packages#sports-club-kit-pack' },
          { name: 'Event Merchandise Pack', href: '/business-clothing-packages#event-merchandise-pack' },
          { name: 'Business Visibility Pack', href: '/business-clothing-packages#business-visibility-pack' },
        ],
        viewAll: { name: 'All Packages', href: '/business-clothing-packages' },
      },
      {
        icon: '🧵',
        title: 'Clothing Brands',
        items: [
          { name: 'Clothing Brand Setup', href: '/clothing-brand-setup' },
          { name: 'Brand Launch Production Pack', href: '/business-clothing-packages#brand-launch-production-pack' },
          { name: '300-Piece Brand Run Case Study', href: '/cat-t-shirt-printing-birmingham-erdington' },
          { name: 'Embroidery Service', href: '/embroidery' },
        ],
        viewAll: { name: 'Client Work', href: '/case-studies' },
      },
    ],
    // Mobile drawer shows a shorter list than the desktop mega menu.
    mobileItems: [
      { name: 'Construction & Trades', href: '/workwear-for-builders-and-trades' },
      { name: 'Clinics, Beauty & Wellness', href: '/embroidered-uniforms-for-clinics-and-salons' },
      { name: 'Restaurants & Hospitality', href: '/restaurant-and-hospitality-uniforms' },
      { name: 'Gyms, Clubs & Sports Teams', href: '/gym-and-sports-club-merchandise' },
      { name: 'Corporate & Office', href: '/corporate-uniforms-and-branded-merchandise' },
      { name: 'Events & Community', href: '/event-t-shirts-and-merchandise' },
      { name: 'Packages & Guide Prices', href: '/business-clothing-packages' },
      { name: 'Clothing Brand Setup', href: '/clothing-brand-setup' },
    ],
    promo: {
      title: 'Get a Business Quote',
      description: 'Send your logo and headcount. Written quote and free mock-up within 24 hours.',
      cta: { name: 'Request a Quote', href: '/quote' },
    },
  },
  'banner-printing': {
    columns: [
      {
        icon: '🖨️',
        title: 'Banner Printing',
        items: [
          { name: '440gsm PVC Banner', href: '/banner-printing/440gsm-pvc-banner' },
          { name: '510gsm Heavy Duty Banner', href: '/banner-printing/510gsm-heavy-duty-banner' },
          { name: 'Mesh Banner Printing', href: '/banner-printing/mesh-banner-printing' },
          { name: '650gsm Event Banner', href: '/banner-printing/650gsm-heavy-duty-event-banner' },
          { name: 'Magnetic Banners', href: '/banner-printing/magnetic-banners' },
          { name: 'Custom Stickers & Labels', href: '/banner-printing/custom-stickers' },
        ],
        viewAll: { name: 'All Banner Printing', href: '/banner-printing' },
      },
    ],
    promo: {
      title: 'Fast Banner Printing',
      description: 'PVC and mesh banners printed in-house in Birmingham with eyelets included.',
      cta: { name: 'Get a Quote', href: '/quote' },
    },
  },
};

const mainNavItems: { name: string; key: string | null; href: string; external?: boolean; compact?: boolean }[] = [
  // compact: shown from xl only — the logo already links home and Design Online is dev-only.
  { name: 'Home', key: null, href: '/', compact: true },
  { name: 'For Business', key: 'business', href: '/branded-workwear' },
  { name: 'Design Online', key: null, href: '/design-your-own', compact: true },
  { name: 'Shop by Product', key: 'shop-by-product', href: '/collections' },
  { name: 'Workwear', key: 'workwear', href: '/collections/workwear' },
  { name: 'Kids', key: 'kids', href: '/collections/kids' },
  { name: 'School Uniform', key: 'school-uniform', href: '/collections/school-uniform' },
  { name: 'Accessories', key: 'accessories', href: '/collections/accessories' },
  { name: 'Catalogue', key: null, href: 'https://oktshirt.mybrandgear.shop/', external: true },
  { name: 'Custom Printing', key: null, href: '/services' },
  { name: 'Banner Printing', key: 'banner-printing', href: '/banner-printing' },
];

const LIVE_SITE_HOSTS = new Set(['oktshirt.co.uk', 'www.oktshirt.co.uk']);

function subscribeToHostnameChange() {
  return () => {};
}

function shouldHideDesignPageLink(): boolean {
  if (typeof window === 'undefined') return true;
  return LIVE_SITE_HOSTS.has(window.location.hostname.toLowerCase());
}

const secondaryNavItems = [
  { name: 'About', href: '/about' },
  { name: 'Blog', href: '/blog' },
  { name: 'Client Work', href: '/case-studies' },
  { name: 'Contact', href: '/contact' },
];

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeMegaMenu, setActiveMegaMenu] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const hideDesignPageLink = useSyncExternalStore(
    subscribeToHostnameChange,
    shouldHideDesignPageLink,
    () => true
  );
  const searchRef = useRef<HTMLDivElement>(null);
  const mobileSearchRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const router = useRouter();
  const { cart, openCart } = useCart();

  // Fetch suggestions as user types
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/search?q=${encodeURIComponent(searchQuery.trim())}`);
        const data = await res.json();
        setSuggestions(data.products || []);
        setShowSuggestions(true);
      } catch {
        setSuggestions([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSearchQueryChange = (value: string) => {
    setSearchQuery(value);
    if (value.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Close suggestions when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideDesktop = searchRef.current?.contains(target);
      const insideMobile = mobileSearchRef.current?.contains(target);
      if (!insideDesktop && !insideMobile) {
        setShowSuggestions(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = searchQuery.trim();
    if (trimmed) {
      setShowSuggestions(false);
      router.push(`/search?q=${encodeURIComponent(trimmed)}`);
      setMobileMenuOpen(false);
    }
  };

  const B2B_PATHS = new Set([
    '/branded-workwear',
    '/business-clothing-packages',
    '/clothing-brand-setup',
    '/workwear-for-builders-and-trades',
    '/embroidered-uniforms-for-clinics-and-salons',
    '/restaurant-and-hospitality-uniforms',
    '/gym-and-sports-club-merchandise',
    '/corporate-uniforms-and-branded-merchandise',
    '/event-t-shirts-and-merchandise',
  ]);
  const isActive = (href: string) => {
    if (href === '/branded-workwear') {
      return B2B_PATHS.has(pathname);
    }
    return pathname === href;
  };
  const { itemCount: wishlistCount } = useWishlist();
  const { customer, isAuthenticated } = useAuth();

  const cartQuantity = cart?.totalQuantity || 0;
  const visibleMainNavItems = hideDesignPageLink
    ? mainNavItems.filter((item) => item.href !== '/design-your-own')
    : mainNavItems;

  return (
    <header className="sticky top-0 z-50 shadow-md">
      {/* Row 1 - Utility Bar */}
      <div className="bg-black text-white">
        <div className="container flex items-center justify-between h-8 text-xs">
          <div className="flex items-center gap-2">
            <span>📍</span>
            <span className="text-gray-300">Birmingham, West Midlands · Over 10 years experience</span>
          </div>
          <div className="hidden md:flex items-center gap-4">
            {/* Secondary links live up here so the main bar has room for For Business */}
            <nav className="hidden lg:flex items-center gap-4 pr-4 border-r border-white/20" aria-label="Secondary">
              {secondaryNavItems.map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className={`transition-colors ${isActive(item.href) ? 'text-white font-semibold' : 'text-gray-300 hover:text-white'}`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
            <a href="mailto:hello@oktshirt.co.uk" className="text-gray-300 hover:text-white transition-colors">
              hello@oktshirt.co.uk
            </a>
            <a href="https://wa.me/447403343433" className="text-green-400 hover:text-green-300 transition-colors flex items-center gap-1">
              <span>💬</span> WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* Row 2 - Logo + Search + Icons */}
      <div className="bg-white border-b border-gray-200">
        <div className="container flex items-center justify-between h-16 lg:h-20">
          {/* Left - Logo */}
          <Link href="/" className="flex-shrink-0">
            <div className="flex flex-col">
              <span className="text-xl lg:text-2xl font-black tracking-tight">
                <span className="text-brand-red">OK</span>
                <span className="text-brand-blue">TSHIRT</span>
              </span>
              <span className="text-[9px] uppercase tracking-[2px] text-gray-500 hidden lg:block">
                Custom Print & Embroidery
              </span>
            </div>
          </Link>

          {/* Center - Search Bar */}
          <div ref={searchRef} className="hidden lg:flex items-center flex-1 mx-8 relative">
            <form onSubmit={handleSearch} className="w-full">
              <div className="flex items-center w-full bg-white rounded-full border border-gray-300">
                <div className="flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full ml-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-5 h-5 text-gray-500"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchQueryChange(e.target.value)}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  placeholder="Search products... e.g. polo t-shirt"
                  className="flex-1 bg-transparent border-none outline-none px-4 py-3 text-sm text-gray-700 placeholder-gray-400"
                />
                <button type="submit" className="bg-brand-blue text-white text-sm font-medium px-6 py-2.5 m-1 rounded-full hover:bg-brand-blue-hover transition-colors">
                  Search
                </button>
              </div>
            </form>

            {/* Search Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-hidden">
                {suggestions.map((item) => (
                  <Link
                    key={item.handle}
                    href={`/products/${item.handle}`}
                    onClick={() => { setShowSuggestions(false); setSearchQuery(''); }}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors"
                  >
                    {item.featuredImage ? (
                      <Image
                        src={item.featuredImage.url}
                        alt={item.featuredImage.altText || item.title}
                        width={40}
                        height={40}
                        className="w-10 h-10 object-cover rounded"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center text-gray-400 text-xs">
                        No img
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
                      <p className="text-sm text-gray-500">{formatPrice(item.price)}</p>
                    </div>
                  </Link>
                ))}
                <Link
                  href={`/search?q=${encodeURIComponent(searchQuery.trim())}`}
                  onClick={() => { setShowSuggestions(false); }}
                  className="block px-4 py-2.5 text-sm font-medium text-center text-black bg-gray-50 hover:bg-gray-100 border-t border-gray-200"
                >
                  View all results
                </Link>
              </div>
            )}
          </div>

          {/* Right - Icons */}
          <div className="flex items-center flex-shrink-0 gap-1 lg:gap-2">
            {/* Quote CTA — quote-first navigation for business buyers */}
            <Link
              href="/quote"
              className="inline-flex items-center justify-center bg-brand-blue text-white text-[11px] sm:text-xs lg:text-sm font-semibold uppercase tracking-wider px-3 sm:px-4 lg:px-5 py-2 lg:py-2.5 mr-1 lg:mr-3 whitespace-nowrap hover:bg-brand-blue-hover transition-colors"
            >
              <span className="sm:hidden">Quote</span>
              <span className="hidden sm:inline">Get a Quote</span>
            </Link>

            {/* Mobile Search */}
            <button className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Search">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
            </button>

            {/* Account */}
            <Link href="/account" className="hidden lg:flex flex-col items-center p-2 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Account">
              {isAuthenticated && customer ? (
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-black text-white text-[10px] font-bold">
                  {(customer.firstName || customer.displayName || '?').charAt(0).toUpperCase()}
                </span>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                </svg>
              )}
              <span className="text-[10px] text-gray-500 font-medium mt-1">
                {isAuthenticated && customer ? (customer.firstName || customer.displayName.split(' ')[0]) : 'Account'}
              </span>
            </Link>

            {/* Wishlist */}
            <Link href="/wishlist" className="hidden lg:flex flex-col items-center p-2 hover:bg-gray-100 rounded-lg transition-colors relative" aria-label="Wishlist">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
              </svg>
              <span className="text-[10px] text-gray-500 font-medium mt-1">Wishlist</span>
              {wishlistCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-[9px] font-bold text-white bg-red-500 rounded-full"
                >
                  {wishlistCount}
                </motion.span>
              )}
            </Link>

            {/* Cart */}
            <button
              onClick={openCart}
              className="flex flex-col items-center p-2 hover:bg-gray-100 rounded-lg transition-colors relative"
              aria-label="Cart"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
              </svg>
              <span className="hidden lg:block text-[10px] text-gray-500 font-medium mt-1">Cart</span>
              {cartQuantity > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-[9px] font-bold text-white bg-black rounded-full"
                >
                  {cartQuantity}
                </motion.span>
              )}
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Menu"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Row 3 - Navigation Bar */}
      <div className="hidden lg:block bg-black border-b border-white/15">
        <div className="container flex items-center justify-between h-12">
          {/* Left - Main Nav. Compact sizing below 2xl so eleven items stay on one line;
              chevrons, Home and the dev-only Design Online link only appear from xl. */}
          <nav className="flex items-center gap-0 2xl:gap-1">
            {visibleMainNavItems.map((item) => (
              <div
                key={item.name}
                className={`relative ${item.compact ? 'hidden xl:block' : ''}`}
                onMouseEnter={() => item.key && setActiveMegaMenu(item.key)}
                onMouseLeave={() => setActiveMegaMenu(null)}
              >
                {item.external ? (
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-1.5 2xl:px-2 py-3 text-[11px] 2xl:text-[12px] font-semibold uppercase tracking-normal 2xl:tracking-wide whitespace-nowrap transition-colors text-gray-400 hover:text-white"
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    href={item.href}
                    className={`flex items-center gap-1 px-1.5 2xl:px-2 py-3 text-[11px] 2xl:text-[12px] font-semibold uppercase tracking-normal 2xl:tracking-wide whitespace-nowrap transition-colors ${isActive(item.href) ? 'text-white' : 'text-gray-400 hover:text-white'}`}
                  >
                    {item.name}
                    {item.key && (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="hidden xl:block w-3 h-3 opacity-50">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                      </svg>
                    )}
                  </Link>
                )}

                {/* Active indicator */}
                {isActive(item.href) && (
                  <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-brand-red" />
                )}

                {/* Mega Menu */}
                {item.key && megaMenus[item.key] && (
                  <AnimatePresence>
                    {activeMegaMenu === item.key && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute left-0 top-full pt-0"
                        style={{ minWidth: '600px' }}
                      >
                        <div className="bg-white border-t-[3px] border-black shadow-2xl">
                          <div className="flex items-stretch p-6 gap-6">
                            {/* Columns */}
                            {megaMenus[item.key].columns.map((column, idx) => (
                              <div key={idx} className="flex-1 min-w-[160px] flex flex-col">
                                <h3 className="text-[13px] font-semibold uppercase tracking-wide text-black mb-3">
                                  {column.title}
                                </h3>
                                <ul className="space-y-2">
                                  {column.items.map((subItem) => (
                                    <li key={subItem.name}>
                                      <Link
                                        href={subItem.href}
                                        className="text-sm text-gray-600 hover:text-black hover:pl-1 transition-all"
                                      >
                                        {subItem.name}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                                {column.viewAll && (
                                  <div className="mt-auto pt-3 border-t border-gray-200">
                                    <Link
                                      href={column.viewAll.href}
                                      className="text-xs font-bold uppercase tracking-wider text-black hover:underline whitespace-nowrap"
                                    >
                                      {column.viewAll.name} →
                                    </Link>
                                  </div>
                                )}
                              </div>
                            ))}

                            {/* Promo Card */}
                            {item.key && megaMenus[item.key]?.promo && (
                              <div className="w-56 bg-gray-100 rounded-lg p-5">
                                <h4 className="font-bold text-sm mb-2">
                                  {megaMenus[item.key].promo?.title}
                                </h4>
                                <p className="text-xs text-gray-600 mb-4">
                                  {megaMenus[item.key].promo?.description}
                                </p>
                                <Link
                                  href={megaMenus[item.key].promo?.cta.href || '#'}
                                  className="inline-block bg-brand-blue text-white text-xs font-semibold px-4 py-2 rounded hover:bg-brand-blue-hover transition-colors"
                                >
                                  {megaMenus[item.key].promo?.cta.name}
                                </Link>
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            ))}
          </nav>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden bg-white border-b border-gray-200 overflow-hidden"
          >
            <div className="container py-4">
              <Link
                href="/quote"
                onClick={() => setMobileMenuOpen(false)}
                className="mb-4 flex items-center justify-center bg-brand-blue py-3 text-sm font-semibold uppercase tracking-wider text-white"
              >
                Get a Quote
              </Link>

              {/* Mobile Search */}
              <div ref={mobileSearchRef} className="relative mb-4">
                <form onSubmit={handleSearch} className="flex items-center bg-gray-100 rounded-lg border border-gray-200">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 ml-3 text-gray-400">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearchQueryChange(e.target.value)}
                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                    placeholder="Search products..."
                    className="flex-1 bg-transparent border-none outline-none px-3 py-3 text-sm"
                  />
                </form>

                {/* Mobile Search Suggestions */}
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
                    {suggestions.map((item) => (
                      <Link
                        key={item.handle}
                        href={`/products/${item.handle}`}
                        onClick={() => { setShowSuggestions(false); setSearchQuery(''); setMobileMenuOpen(false); }}
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors"
                      >
                        {item.featuredImage ? (
                          <Image
                            src={item.featuredImage.url}
                            alt={item.featuredImage.altText || item.title}
                            width={36}
                            height={36}
                            className="w-9 h-9 object-cover rounded"
                          />
                        ) : (
                          <div className="w-9 h-9 bg-gray-100 rounded" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
                          <p className="text-xs text-gray-500">{formatPrice(item.price)}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile Nav Items */}
              <div className="space-y-1">
                {visibleMainNavItems.map((item) => {
                  const menu = item.key ? megaMenus[item.key] : null;
                  const mobileSubItems =
                    menu?.mobileItems ?? (item.key === 'banner-printing' ? menu?.columns[0].items : null);

                  if (item.external) {
                    return (
                      <a
                        key={item.name}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center justify-between py-3 text-base border-b border-gray-100 font-medium text-[var(--color-text-secondary)]"
                      >
                        <span>{item.name}</span>
                      </a>
                    );
                  }

                  return (
                    <div key={item.name} className="border-b border-gray-100">
                      <Link
                        href={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center justify-between py-3 text-base ${isActive(item.href) ? 'font-bold text-black' : 'font-medium text-[var(--color-text-secondary)]'}`}
                      >
                        <span>{item.name}</span>
                      </Link>
                      {mobileSubItems && (
                        <div className="pb-3 pl-4 space-y-2">
                          {mobileSubItems.map((subItem) => (
                            <Link
                              key={subItem.name}
                              href={subItem.href}
                              onClick={() => setMobileMenuOpen(false)}
                              className="block py-1.5 text-sm text-gray-500"
                            >
                              {subItem.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="pt-4 mt-4 border-t border-gray-200">
                  <Link
                    href="/account"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-2 py-3 text-base font-medium"
                  >
                    {isAuthenticated && customer ? (
                      <span className="flex items-center justify-center w-5 h-5 rounded-full bg-black text-white text-[9px] font-bold">
                        {(customer.firstName || customer.displayName || '?').charAt(0).toUpperCase()}
                      </span>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                      </svg>
                    )}
                    {isAuthenticated && customer ? (customer.firstName || customer.displayName.split(' ')[0]) : 'My Account'}
                  </Link>
                  <Link
                    href="/wishlist"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-between py-3 text-base font-medium"
                  >
                    <span className="flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                      </svg>
                      Wishlist
                    </span>
                    {wishlistCount > 0 && (
                      <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                        {wishlistCount}
                      </span>
                    )}
                  </Link>
                  {secondaryNavItems.map((item) => (
                    <Link
                      key={item.name}
                      href={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`block py-2 text-sm ${isActive(item.href) ? 'font-semibold text-black' : 'text-gray-500'}`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
